import { supabase } from "@/integrations/supabase/client";

export interface TwoFactorSettings {
  id: string;
  user_id: string;
  method: 'totp' | 'sms';
  enabled: boolean;
  phone_number?: string;
  secret?: string;
  created_at: string;
  updated_at: string;
}

export const twoFactorService = {
  /**
   * Get user's 2FA settings
   */
  async getSettings(userId: string): Promise<TwoFactorSettings | null> {
    const { data, error }: any = await supabase
      .from('user_2fa')
      .select('id, user_id, method, enabled, phone_number, created_at, updated_at')
      .eq('user_id', userId)
      .maybeSingle();
    
    if (error) {
      console.error('Error fetching 2FA settings:', error);
      return null;
    }
    
    return data as TwoFactorSettings | null;
  },

  /**
   * Get 2FA status (enabled/disabled only, no sensitive data)
   */
  async getStatus(userId: string): Promise<{ enabled: boolean; method?: 'totp' | 'sms' } | null> {
    try {
      const { data, error }: any = await (supabase as any).rpc('get_2fa_status');
      
      if (error) throw error;
      
      if (data && data.length > 0) {
        return {
          enabled: data[0].enabled,
          method: data[0].method
        };
      }
      
      return { enabled: false };
    } catch (error) {
      console.error('Error checking 2FA status:', error);
      return null;
    }
  },

  /**
   * Setup TOTP method
   */
  async setupTOTP(userId: string, secret: string, recoveryCodesHash: string[]): Promise<void> {
    const { error }: any = await supabase
      .from('user_2fa')
      .upsert({
        user_id: userId,
        method: 'totp',
        secret: secret,
        enabled: true,
        recovery_codes_hash: recoveryCodesHash,
        phone_number: null // Clear phone if switching from SMS
      }, {
        onConflict: 'user_id'
      });
    
    if (error) throw error;
  },

  /**
   * Setup SMS method
   */
  async setupSMS(userId: string, phoneNumber: string, recoveryCodesHash: string[]): Promise<void> {
    const { error }: any = await supabase
      .from('user_2fa')
      .upsert({
        user_id: userId,
        method: 'sms',
        phone_number: phoneNumber,
        enabled: true,
        recovery_codes_hash: recoveryCodesHash,
        secret: null // NULL secret for SMS method (not used)
      }, {
        onConflict: 'user_id'
      });
    
    if (error) throw error;
  },

  /**
   * Send SMS verification code
   */
  async sendSMSCode(userId: string): Promise<{ success: boolean; expiresIn: number }> {
    const { data, error } = await supabase.functions.invoke('send-2fa-sms', {
      body: { user_id: userId }
    });
    
    if (error) throw error;
    
    return {
      success: true,
      expiresIn: data?.expires_in || 300 // Default 5 minutes
    };
  },

  /**
   * Verify SMS code
   */
  async verifySMSCode(userId: string, code: string): Promise<boolean> {
    console.log('[2FA] Calling verify_sms_code', { userId, codeLength: code.length });
    
    const { data, error }: any = await (supabase as any).rpc('verify_sms_code', {
      p_user_id: userId,
      p_code: code
    });
    
    console.log('[2FA] verify_sms_code result', { userId, data, error });
    
    if (error) {
      console.error('Error verifying SMS code:', error);
      return false;
    }
    
    return data === true;
  },

  /**
   * Disable 2FA completely
   */
  async disable(userId: string): Promise<void> {
    const { error } = await supabase
      .from('user_2fa')
      .update({ enabled: false })
      .eq('user_id', userId);
    
    if (error) throw error;
  },

  /**
   * Validate phone number (E.164 format)
   */
  validatePhoneNumber(phone: string): boolean {
    // E.164 format: +[country code][number]
    // Must start with +, followed by 1-15 digits
    const e164Regex = /^\+[1-9]\d{1,14}$/;
    return e164Regex.test(phone);
  },

  /**
   * Format phone number for display (masked)
   */
  maskPhoneNumber(phone: string): string {
    if (!phone || phone.length < 4) return phone;
    
    // Extract country code and last 4 digits
    const countryCode = phone.substring(0, 2); // +1, +44, etc.
    const lastFour = phone.slice(-4);
    
    return `${countryCode} (***) ***-${lastFour}`;
  },

  /**
   * Check if current device is trusted
   */
  async isDeviceTrusted(userId: string, deviceId: string): Promise<boolean> {
    try {
      const { data, error } = await supabase.functions.invoke('check-trusted-device', {
        body: { 
          device_id: deviceId,
          action: 'check'
        }
      });

      if (error) throw error;
      return data?.trusted === true;
    } catch (error) {
      console.error('Error checking trusted device:', error);
      return false;
    }
  },

  /**
   * Mark current device as trusted
   */
  async trustDevice(userId: string, deviceId: string, deviceInfo: {
    device_name: string;
    device_type: string;
    browser: string;
    os: string;
    ip_address?: string;
    user_agent?: string;
  }): Promise<boolean> {
    try {
      const { data, error } = await supabase.functions.invoke('check-trusted-device', {
        body: { 
          device_id: deviceId,
          action: 'trust',
          device_info: deviceInfo
        }
      });

      if (error) throw error;
      return data?.success === true;
    } catch (error) {
      console.error('Error trusting device:', error);
      throw error;
    }
  },

  /**
   * Get all trusted devices for user
   */
  async getTrustedDevices(userId: string): Promise<TrustedDevice[]> {
    const { data, error } = await (supabase as any)
      .from('trusted_devices')
      .select('*')
      .eq('user_id', userId)
      .eq('is_active', true)
      .gte('expires_at', new Date().toISOString())
      .order('last_used_at', { ascending: false });

    if (error) throw error;
    return (data || []) as TrustedDevice[];
  },

  /**
   * Revoke a trusted device
   */
  async revokeTrustedDevice(deviceId: string): Promise<void> {
    const { error } = await (supabase as any)
      .from('trusted_devices')
      .delete()
      .eq('id', deviceId);

    if (error) throw error;
  },

  /**
   * Revoke all trusted devices for user
   */
  async revokeAllTrustedDevices(userId: string): Promise<void> {
    const { error } = await (supabase as any)
      .from('trusted_devices')
      .delete()
      .eq('user_id', userId);

    if (error) throw error;
  }
};

export interface TrustedDevice {
  id: string;
  device_id: string;
  device_name: string;
  device_type: string;
  browser: string;
  os: string;
  ip_address: string;
  created_at: string;
  last_used_at: string;
  expires_at: string;
  is_current?: boolean;
}
